import {
  resolveAIModel,
  spreadModelParams,
} from '@src/repositories/resolveAIModel';
import { err, ok } from '@src/services/common/Result';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';

import type { Result } from '@src/services/common/Result';
import type { AIPrompt } from '@src/types.ts/ai_prompts';

/**
 * Title of the DatoCMS **AI Prompt** record this service runs. The record's
 * `description` is the system message; the user's typed question is the user
 * message, so the record needs no `promptFields`.
 *
 * Looked up by title (`AIPromptRepository.getPromptByTitle`), so the title in
 * Dato must match this string exactly — and only one record may carry it.
 */
export const TRANSCRIPT_ANSWER_PROMPT_TITLE =
  'Content Writer | Answer Transcript Question';

/** Same shape as `ClipSearchToken`, so one chat turn can cancel either path. */
export interface TranscriptAnswerToken {
  id: string;
  aborted: boolean;
}

/**
 * Request metadata the backend records against the call. Read fresh on every
 * answer (it is a getter, not a snapshot) so a story switch can't send the
 * previous story's id.
 */
export interface TranscriptAnswerContext {
  storyId?: string;
  storyName?: string;
  userEmail?: string;
  userId?: string;
  /** The `contentGenerationChatGptModel` flag, as the talking-point path reads it. */
  fallbackModel?: string;
}

export interface TranscriptAnswerDeps {
  /** Narrow view of `AIPromptRepository` — this service makes one lookup. */
  promptRepository: {
    getPromptByTitle(title: string): Promise<AIPrompt | undefined>;
  };
  getContext: () => TranscriptAnswerContext;
}

export interface TranscriptAnswerInput {
  /** What the user typed, verbatim. */
  question: string;
  transcriptionText: string;
  /**
   * The previous question and the answer it got, so a follow-up ("tell me
   * more", "shorter") lands on something. **One** turn only: the endpoint
   * takes a single prior exchange, so this is the whole of the memory.
   */
  previousTurn?: { question: string; answer: string };
  token: TranscriptAnswerToken;
  /** Fires per chunk with the text **so far**, so the UI can stream it. */
  onChunk?: (textSoFar: string) => void;
}

export interface TranscriptAnswerOutput {
  text: string;
}

/**
 * Answers a question about the story's transcript, for the Find Clips tab's
 * Ask mode (PLAT-11806 follow-up).
 *
 * It reuses the streaming endpoint the talking-point path already calls
 * (`stream-talking-point-response`): despite the name, its body is generic —
 * a transcript, a system message, a user prompt and a model — which is exactly
 * "answer this question about this transcript", so no backend work is needed.
 *
 * A turn carries at most the previous exchange (`previousTurn`), which is all
 * the endpoint's message list has room for — enough for "tell me more", not
 * for a thread.
 *
 * What it deliberately does **not** reuse is `ChatGPTService.regenerateStreamResponse`
 * and the `contentStudioGeneratedContent` sink: that is keyed by the closed
 * `SupportedGeneratedContentTypes` union and **persists** its result to Dato as
 * a story artifact. A chat turn is ephemeral — the answer lives in the
 * conversation and nowhere else — so it streams to a callback instead.
 *
 * Per CLAUDE.md guardrail 1 this is its own small service rather than another
 * method on `ChatGPTService.ts` (2.7k lines, and heading the way
 * `AIClipProducer` did).
 */
export class TranscriptAnswerService {
  private readonly _deps: TranscriptAnswerDeps;
  private readonly _logger = LogManager.getLogger(
    'service:TranscriptAnswerService',
  );

  constructor(deps: TranscriptAnswerDeps) {
    this._deps = deps;
  }

  async answer(
    input: TranscriptAnswerInput,
  ): Promise<Result<TranscriptAnswerOutput>> {
    const question = input.question.trim();
    if (!question) {
      return err('VALIDATION', 'Ask a question first.');
    }
    if (!input.transcriptionText.trim()) {
      return err('PRECONDITION', 'This story has no transcript to read yet.');
    }

    try {
      const prompt = await this._deps.promptRepository.getPromptByTitle(
        TRANSCRIPT_ANSWER_PROMPT_TITLE,
      );
      if (!prompt) {
        // A missing record is a content problem, not the user's doing — say so
        // plainly on screen and put the title that was looked up in the log,
        // which is the only thing that makes it fixable.
        this._logger.error('Transcript answer prompt is missing from Dato', {
          title: TRANSCRIPT_ANSWER_PROMPT_TITLE,
        });
        return err(
          'PRECONDITION',
          "Answering isn't set up yet. Please let us know so we can switch it on.",
        );
      }

      const context = this._deps.getContext();
      const resolved = resolveAIModel(
        null,
        prompt.model,
        context.fallbackModel,
      );

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/aiprompts/stream-talking-point-response`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            transcript: input.transcriptionText,
            systemMessage: prompt.description,
            // The endpoint appends `prevPrompt`/`prevContent` as a user +
            // assistant pair before the new question — but only when
            // `prevContent` is non-empty, which is what makes a follow-up work.
            // With no prior turn the question stands alone.
            prevPrompt: input.previousTurn?.question ?? question,
            prevContent: input.previousTurn?.answer ?? '',
            currentPrompt: question,
            temperature: 1,
            model: resolved?.model_id,
            ...spreadModelParams(resolved),
            prompt_title: prompt.title,
            prompt_internal_title: prompt.internalTitle,
            prompt_type: prompt.promptType,
            prompt_id: prompt.id,
            storyId: context.storyId,
            story_name: context.storyName,
            user_email: context.userEmail,
            user_id: context.userId,
          }),
        },
      );

      if (!response.ok || !response.body) {
        this._logger.error('Transcript answer request failed', {
          status: response.status,
        });
        return err('EXTERNAL', 'That answer failed. Please try again.');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let text = '';

      // Cancellation is checked between chunks rather than through an
      // AbortSignal: the token is what the chat already holds, and cancelling
      // the reader closes the body stream. A turn stopped before the first
      // byte still waits for the response head — the cost of not threading a
      // second cancellation mechanism through the hook.
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        if (input.token.aborted) {
          await reader.cancel();
          return err('PRECONDITION', 'Answer cancelled.');
        }
        text += decoder.decode(value, { stream: true });
        input.onChunk?.(text);
      }

      if (!text.trim()) {
        this._logger.warn('Transcript answer stream closed with no text');
        return err(
          'EXTERNAL',
          'Nothing came back for that. Try asking another way.',
        );
      }

      return ok({ text });
    } catch (e) {
      this._logger.error('Transcript answer failed', extractErrorInfo(e));
      return err(
        'EXTERNAL',
        'That answer failed. Please try again.',
        extractErrorInfo(e),
      );
    }
  }
}
